/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PtacCustomSelectionType } from 'data/types';
import { DEFAULT_PTAC_ELECTRICAL_FILTER } from 'utils/ptac-electrical-filter';
import {
  PtacSelectionFilters,
  PtacSelectionState,
  PtacSystemsFetchFinish,
  PtacSystemsFilterUniverse
} from './ptac-selection-types';

export const defaultPtacSelectionFilters: PtacSelectionFilters = {
  terminalType: null,
  manufacturers: null,
  series: null,
  voltage: DEFAULT_PTAC_ELECTRICAL_FILTER,
  amperage: DEFAULT_PTAC_ELECTRICAL_FILTER
};

export const emptyPtacSystemsFilterUniverse: PtacSystemsFilterUniverse = {
  manufacturers: [],
  series: [],
  terminalTypes: [],
  voltages: [],
  amperages: []
};

export const defaultPtacSelectionState: PtacSelectionState = {
  systems: {
    status: 'idle',
    requestBodyHash: null,
    options: [],
    unitPicksByOptionId: {},
    alternativesByOptionId: {},
    filterUniverse: emptyPtacSystemsFilterUniverse
  },
  filters: defaultPtacSelectionFilters,
  customSelections: []
};

const ptacSelectionSlice = createSlice({
  name: 'ptacSelection',
  initialState: defaultPtacSelectionState,
  reducers: {
    beginPtacSystemsFetch: (
      state,
      action: PayloadAction<{ requestBodyHash: string }>
    ) => {
      state.systems.status = 'loading';
      state.systems.requestBodyHash = action.payload.requestBodyHash;
    },
    // Latest-wins: a finish whose hash no longer matches the latest request
    // is stale and dropped (the ducted-compatibility contract).
    finishPtacSystemsFetch: (
      state,
      action: PayloadAction<PtacSystemsFetchFinish>
    ) => {
      const { requestBodyHash, data } = action.payload;
      if (state.systems.requestBodyHash !== requestBodyHash) return;
      state.systems = { ...data, status: 'loaded', requestBodyHash };
    },
    failPtacSystemsFetch: (
      state,
      action: PayloadAction<{ requestBodyHash: string }>
    ) => {
      if (state.systems.requestBodyHash !== action.payload.requestBodyHash) {
        return;
      }
      state.systems.status = 'error';
    },
    // Forgets the last request so the fetch hook re-issues it.
    retryPtacSystemsFetch: (state) => {
      state.systems.status = 'idle';
      state.systems.requestBodyHash = null;
    },
    setPtacSelectionFilters: (
      state,
      action: PayloadAction<Partial<PtacSelectionFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    addPtacCustomSelection: (
      state,
      action: PayloadAction<PtacCustomSelectionType>
    ) => {
      state.customSelections = [
        ...state.customSelections.filter(
          (selection) => selection.id !== action.payload.id
        ),
        action.payload
      ];
    },
    removePtacCustomSelection: (state, action: PayloadAction<string>) => {
      state.customSelections = state.customSelections.filter(
        (selection) => selection.id !== action.payload
      );
    },
    resetPtacSelection: () => defaultPtacSelectionState
  }
});

export const {
  beginPtacSystemsFetch,
  finishPtacSystemsFetch,
  failPtacSystemsFetch,
  retryPtacSystemsFetch,
  setPtacSelectionFilters,
  addPtacCustomSelection,
  removePtacCustomSelection,
  resetPtacSelection
} = ptacSelectionSlice.actions;

export default ptacSelectionSlice.reducer;
